import { useState, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useApi } from "../Api";
import { useUserProfile } from "../UserProfileContext";
import { PageMasthead } from "../components/PageMasthead";

interface ProfileData {
  displayName?: string;
  bio?: string;
  [key: string]: any;
}

const ProfilePage: React.FC = () => {
  const { user } = useAuth0();
  const api = useApi();
  const { updateDisplayName } = useUserProfile();
  const [profileData, setProfileData] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string>("");

  useEffect(() => {
    const fetchProfileData = async (): Promise<void> => {
      try {
        setLoading(true);

        const response = await api.get("/api/profile/GetProfile");

        setProfileData(response.data);
      } catch (err) {
        console.error("Error fetching profile:", err);
        setError("Failed to load profile data");
      } finally {
        setLoading(false);
      }
    };

    fetchProfileData();
  }, [api]);

  const updateProfile = async (updatedData: ProfileData): Promise<void> => {
    try {
      const response = await api.put("/api/profile/UpdateProfile",
        updatedData
      );

      setProfileData(response.data);
      // Update the global context so other components (Navbar, Dashboard) reflect the change
      if (updatedData.displayName) {
        updateDisplayName(updatedData.displayName);
      }
      setMessage("Profile updated successfully!");
      setTimeout(() => setMessage(""), 3000);
    } catch (err) {
      console.error("Error updating profile:", err);
      setError("Failed to update profile");
      setTimeout(() => setError(null), 3000);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <PageMasthead eyebrow="Account" title="Profile" subtitle="Manage your account information" />

      {/* Messages */}
      {message && (
        <div className="alert-success">
          {message}
        </div>
      )}

      {error && (
        <div className="alert-danger">
          {error}
        </div>
      )}
      {/* Auth0 User Info */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <h2 className="text-xl font-semibold mb-4">
          Account Information
        </h2>
        <div className="bg-gray-50 rounded-lg p-6">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name
                  </label>
                  <p className="text-gray-900">
                    {user?.name || "Not provided"}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <p className="text-gray-900">
                    {user?.email || "Not provided"}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email Verified
                  </label>
                  <p className="text-gray-900">
                    {user?.email_verified ? "Yes" : "No"}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    User ID
                  </label>
                  <p className="text-gray-900 text-sm font-mono">{user?.sub}</p>
                </div>
              </div>
            </div>
          </div>

      {/* Custom Profile Data */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <h2 className="text-xl font-semibold mb-4">
          Profile Details
        </h2>
        {profileData ? (
          <div className="bg-gray-50 rounded-lg p-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Display Name
                </label>
                <input
                  type="text"
                  value={profileData.displayName || ""}
                  onChange={(e) =>
                    setProfileData({
                      ...profileData,
                      displayName: e.target.value,
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Bio
                </label>
                <textarea
                  value={profileData.bio || ""}
                  onChange={(e) =>
                    setProfileData({ ...profileData, bio: e.target.value })
                  }
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => updateProfile(profileData)}
                className="btn-primary"
              >
                Save Changes
              </button>
            </div>
          </div>
            ) : (
              <div className="bg-gray-50 rounded-lg p-6 text-center">
                <p className="text-gray-600 mb-4">No profile data available</p>
                <button
                  onClick={() =>
                    updateProfile({ displayName: user?.name || "", bio: "" })
                  }
                  className="btn-primary"
                >
                  Create Profile
                </button>
              </div>
            )}
          </div>

      {/* API Status */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <h2 className="text-xl font-semibold mb-4">
          API Connection
        </h2>
        <div className="bg-gray-50 rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-900 font-medium">
                    Backend API Status
                  </p>
                  <p className="text-gray-600 text-sm">
                    Connection to .NET Core API
                  </p>
                </div>
                <div
                  className={`w-4 h-4 rounded-full ${
                    profileData ? "bg-green-500" : "bg-red-500"
                  }`}
                ></div>
              </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
